import React, { useEffect, useRef, useState, type FC } from 'react';
import { Input } from '../fields/input';
import styled, { css } from 'styled-components';
import {
  borderRadius,
  neutral600,
  default100,
  neutral200,
  neutral0,
  fontSizeBodySM,
  fontWeightRegular,
  spacing1,
  spacing13,
  spacing2,
  spacing3,
  spacing6,
} from 'lib/mortar/design-tokens';
import { scrollTo } from '@utils/scroller';
import { ErrorMessage, InputGroup } from '../fields/field.style';
import type { IconSize } from '../Icons';

export const NO_MATCH_TEXT = 'There are no options that match your search';
export const ENTER_A_VALUE = 'Enter a value to see some suggested options';

const Wrapper = styled.div<StyledDiv>`
  display: flex;
  flex-direction: column;
  width: 100%;
`;
const Container = styled.div<StyledDiv & { isActive: boolean; ref: any }>`
  position: relative;
  width: 100%;
  display: flex;
  flex-direction: column;
  background: ${neutral0};

  ${({ isActive }) =>
    isActive &&
    css`
      box-shadow:
        0px ${spacing2} ${spacing3} -${spacing1} rgba(16, 24, 40, 0.06),
        0px ${spacing1} ${spacing3} -${spacing1} rgba(16, 24, 40, 0.03);
    `}
`;
const SearchBar = styled(Input)`
  ${InputGroup} {
    ${({ showFocus }) =>
      showFocus &&
      css`
        border-bottom-right-radius: 0;
        border-bottom-left-radius: 0;
      `}
  }
`;
const ListWrapper = styled.ul<StyledUnorderedList>`
  position: absolute;
  width: 100%;
  z-index: 2;
  border: 1px solid ${neutral200};
  border-top: none;
  border-radius: 0 0 ${borderRadius()} ${borderRadius()};
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  max-height: 180px;
  overflow-y: auto;
  box-sizing: border-box;
  gap: ${spacing1};
  background: ${neutral0};
  top: 52px; // sets it right underneath the input

  ::-webkit-scrollbar {
    width: ${spacing2};
    background: ${neutral0};
  }
  ::-webkit-scrollbar-thumb {
    background: ${neutral200};
    border-radius: ${spacing1};
    min-height: ${spacing13};
  }
`;

const ListItem = styled.li<
  StyledListItem & {
    highlighted: boolean;
    disabled?: boolean;
  }
>`
  padding: ${spacing3};
  font-weight: ${fontWeightRegular};
  font-size: ${fontSizeBodySM};
  line-height: ${spacing6};
  background: ${neutral0};

  &:hover {
    background: ${default100};
  }

  ${({ highlighted }) =>
    highlighted &&
    css`
      background: ${default100};
    `}

  ${({ disabled }) =>
    disabled &&
    css`
      background: none;
      color: ${neutral600};
    `}
`;

export type DropdownOption = {
  id: number | string;
  label: string;
  parent?: {
    id: number | string;
  };
};

export type DropdownSearchProps = {
  className?: string;
  name: string;
  options: DropdownOption[];
  value?: DropdownOption;
  label?: string;
  placeholder?: string;
  onChange: (name: string, option?: DropdownOption) => void;
  onInput?: (value: string) => void;
  errorMessage?: string;
  dataTrackId?: string;
  autofilter?: boolean;
  ref?: React.Ref<HTMLInputElement>;
  leftIcon?: FC<{ size: IconSize; color: string; [key: string]: unknown }>;
  leftIconProps?: Record<string, any>;
  rightIcon?: FC<{ size: IconSize; color: string; [key: string]: unknown }>;
  rightIconProps?: Record<string, any>;
  initialSearchPhrase?: string;
  autocomplete?: string;
};

export const DropdownSearch = ({
  className,
  name,
  options,
  value: selectedOption,
  label,
  placeholder,
  onChange,
  onInput,
  errorMessage,
  dataTrackId,
  autofilter = true,
  leftIcon: LeftIcon,
  leftIconProps,
  rightIcon: RightIcon,
  rightIconProps,
  initialSearchPhrase = '',
  autocomplete = 'off',
}: DropdownSearchProps) => {
  const listId = `${name}-option-list`;
  const WrapperRef = useRef<HTMLDivElement>(null);

  const [searchPhrase, setSearchPhrase] = useState<string>(initialSearchPhrase);
  useEffect(() => {
    if (initialSearchPhrase !== searchPhrase) {
      setSearchPhrase(initialSearchPhrase);
    }
  }, [initialSearchPhrase]);

  const [highlightedOption, setHighlightedOption] = useState<
    DropdownOption | undefined
  >();
  const [focus, setFocus] = useState<boolean>(false);

  useEffect(() => {
    const handleClickOutside = (event: Event) => {
      if (
        focus &&
        WrapperRef.current &&
        !WrapperRef.current.contains(event.target as HTMLElement)
      ) {
        setFocus(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [focus]);

  const onSearchChange = (e: { target: { name: string; value: unknown } }) => {
    setFocus(true);
    setSearchPhrase(e.target.value as string);
    onInput?.(e.target.value as string);
  };

  const filteredOptions = autofilter
    ? options.filter(option =>
        option.label.toLowerCase().includes(searchPhrase.toLowerCase()),
      )
    : options;

  const onOptionSelected = (option: DropdownOption) => {
    onChange(name, option);
    setFocus(false);
    setHighlightedOption(undefined);
  };

  const onHighlightOption = (option: DropdownOption) => {
    setHighlightedOption(option);
    scrollTo(`${listId}-option-${option.id}`, {
      behavior: 'smooth',
      block: 'nearest',
      inline: 'start',
    });
  };

  const onHighlightNextOption = () => {
    const index = highlightedOption
      ? filteredOptions.findIndex(
          option => option.id === highlightedOption.id,
        ) + 1
      : 0;
    onHighlightOption(
      filteredOptions[index < filteredOptions.length ? index : 0],
    );
  };

  const onHighlightPrevOption = () => {
    const index = highlightedOption
      ? filteredOptions.findIndex(
          option => option.id === highlightedOption.id,
        ) - 1
      : 0;

    onHighlightOption(
      filteredOptions[index > -1 ? index : filteredOptions.length - 1],
    );
  };

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') {
      setFocus(true);
      onHighlightNextOption();
    }

    if (e.key === 'ArrowUp') {
      setFocus(true);
      onHighlightPrevOption();
    }

    if (e.key === 'Enter' && highlightedOption) {
      e.stopPropagation();
      onOptionSelected(highlightedOption);
    }

    if (e.key === 'Escape' || e.key === 'Tab') {
      setFocus(false);
    }
  };

  const textDisplay = focus ? searchPhrase : selectedOption?.label || '';

  return (
    <Wrapper className={className}>
      <Container
        isActive={focus}
        ref={WrapperRef}
        onFocus={() => setFocus(true)}
        onKeyDown={onKeyDown}
      >
        <SearchBar
          name={name}
          value={textDisplay}
          onChange={onSearchChange}
          label={label}
          placeholder={placeholder}
          autoComplete={autocomplete}
          leftIcon={LeftIcon}
          leftIconProps={leftIconProps}
          rightIcon={RightIcon}
          rightIconProps={rightIconProps}
          role="combobox"
          aria-controls={listId}
          aria-autocomplete="list"
          aria-expanded={focus}
          aria-activedescendant={
            highlightedOption
              ? `${listId}-option-${highlightedOption.id}`
              : undefined
          }
          aria-haspopup="listbox"
          showFocus={focus}
          data-track-id={dataTrackId}
          showError={!!errorMessage}
        />
        {focus && (
          <ListWrapper id={listId} role="listbox">
            {filteredOptions.length > 0 ? (
              filteredOptions.map(option => (
                <ListItem
                  key={option.id}
                  id={`${listId}-option-${option.id}`}
                  onClick={() => onOptionSelected(option)}
                  highlighted={
                    highlightedOption
                      ? highlightedOption.id === option.id
                      : false
                  }
                  role="option"
                >
                  {option.label}
                </ListItem>
              ))
            ) : (
              <ListItem highlighted={false} disabled>
                {textDisplay ? NO_MATCH_TEXT : ENTER_A_VALUE}
              </ListItem>
            )}
          </ListWrapper>
        )}
      </Container>
      {errorMessage && <ErrorMessage>{errorMessage}</ErrorMessage>}
    </Wrapper>
  );
};
